import parse, { DOMNode, domToReact } from 'html-react-parser';
import { componentsByTagName } from '../../grapesJs/reactComponentsRegistry/reactComponentsRegistry';

/**
 * Преобразует HTML-строку в структуру React-компонентов.
 * Заменяет кастомные HTML-теги на соответствующие React-компоненты из реестра
 */
export function parseHtml(html: string) {
  // Создаем объект параметров для парсера с функцией замены элементов
  const parseOptions = {
    replace(domNode: DOMNode) {
      // Обрабатываем только теги, пропускаем текстовые узлы, комментарии и т.д.
      if (domNode.type !== 'tag') {
        return domNode;
      }

      // Получаем имя тега в нижнем регистре для поиска в реестре
      const tagName = domNode.name?.toLowerCase();

      // Ищем соответствующий React-компонент в реестре по имени тега
      const compDef = componentsByTagName[tagName];

      // Если нашли соответствующий React-компонент в реестре
      if (compDef) {
        const Component = compDef.component;
        const traits = compDef.traits;

        // Преобразуем HTML-атрибуты в React-пропсы с учетом метаданных (traits)
        const attribs = Object.entries(domNode.attribs || {}).reduce(
          (acc, [key, value]) => {
            // Ищем соответствующее свойство в метаданных компонента
            const trait = traits?.find(trait => {
              if (typeof trait === 'string') {
                return trait.toLowerCase() === key.toLowerCase();
              }

              return trait.name?.toLowerCase() === key.toLowerCase();
            });

            // Системные поля, которых нет в traits, сохраняем как есть
            if (!trait) {
              acc[key] = value;

              return acc;
            }

            // Для строковых traits сохраняем оригинальный регистр имени свойства
            if (typeof trait === 'string') {
              acc[trait] = value;

              return acc;
            }

            // Для булевых полей (checkbox) устанавливаем значение true
            if (trait.type === 'checkbox') {
              acc[trait.name || key] = true;

              return acc;
            }

            // Для всех остальных типов сохраняем значение как есть
            acc[trait.name || key] = value;

            return acc;
          },
          {} as Record<string, string | boolean>,
        );

        // Создаем React-компонент с подготовленными пропсами
        // Рекурсивно обрабатываем дочерние элементы, чтобы преобразовать вложенные компоненты
        return (
          <Component {...attribs}>
            {domNode.children && domToReact(domNode.children as DOMNode[], parseOptions)}
          </Component>
        );
      }

      // Если компонент не найден в реестре, обрабатываем как обычный HTML-элемент
      // Важно вызвать domToReact для дочерних элементов, чтобы обработать вложенные React-компоненты
      if (domNode.children) {
        return {
          ...domNode,
          children: domToReact(domNode.children as DOMNode[], parseOptions),
        };
      }

      // Возвращаем узел без изменений, если у него нет дочерних элементов
      return domNode;
    },
  };

  // Запускаем парсинг HTML-строки с настроенными параметрами
  // Результатом будет структура React-элементов
  return parse(html, parseOptions);
}
